import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  Dispatch,
  SetStateAction,
  ReactElement,
  useMemo
} from 'react';
import { CurrentUsageData, RolloverData, UsageData } from 'src/types/usage';

interface DataProviderProps {
  children: ReactNode;
}

interface DataContextProps {
  usageData: UsageData[];
  currentUsageData: CurrentUsageData;
  rolloverData: RolloverData;
  setUsageData: Dispatch<SetStateAction<UsageData[]>>;
  setRolloverData: Dispatch<SetStateAction<RolloverData>>;
  setCurrentUsageData: Dispatch<SetStateAction<CurrentUsageData>>;
  selectedPrinterSku?: (printerName?: string) => void;
  setSelectedPrinterSku?: Dispatch<
    SetStateAction<((printerName?: string) => void) | undefined>
  >;
}

const DataContext = createContext<DataContextProps | undefined>(undefined);

export const useData = (): DataContextProps | undefined =>
  useContext(DataContext);

export const DataProvider = ({ children }: DataProviderProps): ReactElement => {
  const [usageData, setUsageData] = useState<UsageData[] | null>(null);
  const [currentUsageData, setCurrentUsageData] =
    useState<CurrentUsageData | null>(null);
  const [rolloverData, setRolloverData] = useState<RolloverData | null>(null);
  const [selectedPrinterSku, setSelectedPrinterSku] = useState<
    ((printerName?: string) => void) | undefined
  >(undefined);

  const value = useMemo(
    () => ({
      usageData,
      currentUsageData,
      rolloverData,
      setUsageData,
      setCurrentUsageData,
      setRolloverData,
      selectedPrinterSku,
      setSelectedPrinterSku
    }),
    [usageData, currentUsageData, rolloverData, selectedPrinterSku]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
